import { useEffect, useId, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { DateInput, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import {
  createDocumentRequest,
  DOCUMENT_REQUEST_PRIORITIES,
  documentRequestPriorityLabel,
  type DocumentRequestKind,
  type DocumentRequestPriority,
} from "@/lib/documentRequest";
import {
  documentKindWorkbenchFieldOptions,
  fetchDocumentLookups,
  type ControlledDocument,
} from "@/lib/documents";
import { hubSupportTicketPagePath } from "@/lib/navigationTrail";

const NEW_DOCUMENT_MODAL_HELP =
  "Describe the controlled document you need for this part number. Document Control will review and respond in the request queue.";

export function DocumentRequestSubmitModal(props: {
  open: boolean;
  kind: DocumentRequestKind;
  sku: string;
  document?: Pick<ControlledDocument, "id" | "title"> | null;
  onClose: () => void;
  onSubmitted?: (requestId: string) => void;
}): JSX.Element | null {
  const formId = useId();
  const { showToast } = useHubToast();
  const [body, setBody] = useState("");
  const [documentKindCode, setDocumentKindCode] = useState("");
  const [documentKindSearch, setDocumentKindSearch] = useState("");
  const [priority, setPriority] = useState<DocumentRequestPriority | "">("");
  const [requestedByDate, setRequestedByDate] = useState("");

  const lookupsQuery = useQuery({
    queryKey: ["document-lookups"],
    queryFn: fetchDocumentLookups,
    enabled: props.open && props.kind === "new_document",
  });

  const documentKindOptions = useMemo(
    () => documentKindWorkbenchFieldOptions(lookupsQuery.data?.kinds ?? []),
    [lookupsQuery.data?.kinds],
  );

  const priorityOptions = useMemo(
    () =>
      DOCUMENT_REQUEST_PRIORITIES.map((code) => ({
        value: code,
        label: documentRequestPriorityLabel(code),
      })),
    [],
  );

  useEffect(() => {
    if (props.open) {
      setBody("");
      setDocumentKindCode("");
      setDocumentKindSearch("");
      setPriority("");
      setRequestedByDate("");
    }
  }, [props.open, props.kind, props.document?.id]);

  const submitMutation = useMutation({
    mutationFn: (payload: {
      kind: DocumentRequestKind;
      sku: string;
      documentRecordId?: string;
      documentKindCode?: string | null;
      priority?: DocumentRequestPriority | null;
      requestedByDate?: string | null;
      body: string;
      pagePath?: string | null;
    }) => createDocumentRequest(payload),
    onSuccess: (result) => {
      showToast({
        tone: "success",
        message: `Document request #${result.id} was submitted.`,
      });
      props.onSubmitted?.(result.id);
      props.onClose();
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  if (!props.open) {
    return null;
  }

  const title =
    props.kind === "access"
      ? `Request access — ${props.document?.title ?? "document"}`
      : `Request a document for ${props.sku}`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${formId}-title`}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
        <div className="flex items-center gap-2">
          <h2 id={`${formId}-title`} className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
          {props.kind === "new_document" ? (
            <HoverHintWithGlyph hoverHint={NEW_DOCUMENT_MODAL_HELP} size="md" />
          ) : null}
        </div>
        {props.kind === "access" ? (
          <p className="mt-1 text-sm text-slate-600">
            Document Control will review your request. You can track status under My document requests in the account
            menu.
          </p>
        ) : null}
        <form
          id={formId}
          className="mt-4 space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (body.trim() === "") {
              showToast({ tone: "error", message: "Please describe what you need." });
              return;
            }
            if (props.kind === "new_document" && priority === "") {
              showToast({ tone: "error", message: "Please select a priority." });
              return;
            }
            void submitMutation.mutateAsync({
              kind: props.kind,
              sku: props.sku,
              documentRecordId: props.kind === "access" ? props.document?.id : undefined,
              documentKindCode:
                props.kind === "new_document" && documentKindCode.trim() !== ""
                  ? documentKindCode.trim()
                  : null,
              priority:
                props.kind === "new_document" && priority !== "" ? priority : null,
              requestedByDate:
                props.kind === "new_document" && requestedByDate.trim() !== "" ? requestedByDate.trim() : null,
              body: body.trim(),
              pagePath: hubSupportTicketPagePath(window.location.pathname, window.location.search),
            });
          }}
        >
          {props.kind === "new_document" ? (
            <>
              <div>
                <Label htmlFor={`${formId}-document-kind`}>Document type (optional)</Label>
                <SearchableSelect
                  id={`${formId}-document-kind`}
                  className="mt-1"
                  value={documentKindCode}
                  onValueChange={(option) => {
                    setDocumentKindCode(option?.value ?? "");
                    setDocumentKindSearch("");
                  }}
                  options={documentKindOptions}
                  searchValue={documentKindSearch}
                  onSearchChange={setDocumentKindSearch}
                  placeholder="Not set"
                  searchPlaceholder="Search document types"
                  emptyMessage="No matching document types."
                  isLoading={lookupsQuery.isLoading}
                />
              </div>
              <div>
                <Label htmlFor={`${formId}-priority`}>Priority</Label>
                <select
                  id={`${formId}-priority`}
                  required
                  value={priority}
                  onChange={(event) => setPriority(event.target.value as DocumentRequestPriority | "")}
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                >
                  <option value="" disabled>
                    Select priority…
                  </option>
                  {priorityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor={`${formId}-requested-by-date`}>Request by date (optional)</Label>
                <DateInput
                  id={`${formId}-requested-by-date`}
                  value={requestedByDate}
                  onChange={(event) => setRequestedByDate(event.target.value)}
                  className="mt-1"
                />
              </div>
            </>
          ) : null}
          <div>
            <Label htmlFor={`${formId}-body`}>What do you need and why?</Label>
            <textarea
              id={`${formId}-body`}
              required
              rows={4}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder={
                props.kind === "access"
                  ? "Your role and how you will use this controlled document…"
                  : "Which document is missing and why you need it…"
              }
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={props.onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitMutation.isPending}>
              {submitMutation.isPending ? "Submitting…" : "Submit request"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
